import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Calendar, Users, MapPin, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import OptimizedImage from '../components/OptimizedImage';

const EventsExhibitions = () => {
  const pastProjects = [
    {
      id: 1,
      title: "International Auto Expo 2024",
      category: "Corporate Event",
      description: "A grand 3-day automotive exhibition featuring 50+ brands, with 10,000+ attendees. Included live product launches, test drives, and celebrity appearances.",
      client: "Elite Automotive Group",
      attendees: "10,000+",
      date: "March 2024"
    },
    {
      id: 2,
      title: "Luxury Fashion Week Bangkok",
      category: "Fashion Event",
      description: "A week-long fashion extravaganza with runway shows, designer exhibitions, and exclusive afterparties. Featured 30 international designers.",
      client: "Bangkok Fashion Council",
      attendees: "5,000+",
      date: "February 2024"
    },
    {
      id: 3,
      title: "Tech Innovation Summit",
      category: "MICE Event",
      description: "International technology conference with keynote speakers, breakout sessions, and tech exhibitions. Included virtual participation for 2,000+ remote attendees.",
      client: "Global Tech Corp",
      attendees: "3,500+",
      date: "January 2024"
    },
    {
      id: 4,
      title: "Beachside Corporate Retreat",
      category: "Corporate Event",
      description: "3-day team building and strategy session for executive leadership. Featured luxury accommodations, workshops, and gala dinner.",
      client: "Fortune 500 Company",
      attendees: "200+",
      date: "December 2023"
    },
    {
      id: 5,
      title: "Annual Charity Gala",
      category: "Gala Dinner",
      description: "Black-tie charity event with live auction, entertainment, and 5-course dinner. Raised over $500,000 for local children's hospital.",
      client: "Hope Foundation Thailand",
      attendees: "400+",
      date: "November 2023"
    },
    {
      id: 6,
      title: "Product Launch Spectacular",
      category: "Brand Activation",
      description: "Immersive product launch experience with augmented reality demos, influencer engagement, and media coverage from 50+ outlets.",
      client: "Tech Innovations Inc",
      attendees: "800+",
      date: "October 2023"
    }
  ];

  const services = [
    "Live Concerts & Entertainment",
    "MICE (National & International)",
    "Corporate Events & Brand Activations",
    "Product Launches & Gala Dinners",
    "Beach & Destination Weddings",
    "Dealer Meets & Conferences",
    "Exhibition Design & Management",
    "Venue Sourcing & Management",
    "Audio-Visual Production",
    "Stage Design & Fabrication",
    "Catering & Hospitality",
    "Guest Management & Registration"
  ];

  return (
    <div className="bg-dark-bg">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 z-0">
          <OptimizedImage
            src="/JB2.jpg"
            alt="Events & Exhibitions"
            className="w-full h-full object-cover"
            priority={true}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black"></div>
        </div>

        <div className="relative z-10 container mx-auto px-6 lg:px-12 text-center pt-32 pb-20">
          <p className="text-gold text-sm tracking-widest uppercase mb-4">Premium Service</p>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-silver leading-tight">
            Events &
            <span className="gold-gradient-text block mt-2">Exhibitions</span>
          </h1>
          <p className="text-gray-300 text-lg md:text-xl max-w-3xl mx-auto mt-6">
            Creating extraordinary experiences that captivate, engage, and inspire. From intimate gatherings to grand exhibitions, we bring your vision to life with precision and elegance.
          </p>
          <div className="flex justify-center mt-8">
            <div className="relative w-96 h-0.5 bg-gradient-to-r from-transparent via-gold to-transparent shadow-lg">
              <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gold rounded-full shadow-lg"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-32 bg-gradient-to-b from-black to-dark-grey">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto text-center mb-16 space-y-4">
            <p className="text-gold text-sm tracking-widest uppercase">What We Offer</p>
            <h2 className="text-4xl md:text-5xl font-bold text-silver">
              Comprehensive Event
              <span className="gold-gradient-text"> Solutions</span>
            </h2>
            <p className="text-gray-300 text-lg">
              End-to-end event management services designed to exceed your expectations.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {services.map((service, index) => (
              <div key={index} className="flex items-start gap-3 group">
                <CheckCircle2 className="text-gold flex-shrink-0 mt-1 group-hover:scale-110 transition-transform" size={20} />
                <span className="text-gray-300">{service}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Past Projects */}
      <section className="py-32 bg-black">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <p className="text-gold text-sm tracking-widest uppercase">Our Portfolio</p>
            <h2 className="text-4xl md:text-5xl font-bold text-silver">
              Past
              <span className="gold-gradient-text"> Projects</span>
            </h2>
            <p className="text-gray-300 text-lg">
              A showcase of successful events that demonstrate our expertise and commitment to excellence.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {pastProjects.map((project) => (
              <Card key={project.id} className="bg-dark-grey border-gold/20 hover:border-gold transition-all duration-300 group">
                <div className="p-8 space-y-6">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-gold text-xs tracking-wider uppercase">{project.category}</span>
                      <span className="text-gray-400 text-xs">{project.date}</span>
                    </div>
                    <h3 className="text-2xl font-bold text-silver group-hover:text-gold transition-colors">
                      {project.title}
                    </h3>
                    <p className="text-gray-300 leading-relaxed">
                      {project.description}
                    </p>
                  </div>

                  <div className="space-y-3 pt-4 border-t border-gold/10">
                    <div className="flex items-center gap-2 text-sm">
                      <Users className="text-gold" size={16} />
                      <span className="text-gray-400">Attendees:</span>
                      <span className="text-silver font-semibold">{project.attendees}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <TrendingUp className="text-gold" size={16} />
                      <span className="text-gray-400">Client:</span>
                      <span className="text-silver">{project.client}</span>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 bg-black">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h2 className="text-4xl md:text-5xl font-bold text-silver">
              Ready to Create Your
              <span className="gold-gradient-text"> Next Event?</span>
            </h2>
            <p className="text-gray-300 text-lg max-w-2xl mx-auto">
              Let's discuss how we can bring your vision to life with an unforgettable event experience.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-6">
              <Link to="/contact">
                <Button variant="outline" className="border-2 border-gold text-silver hover:bg-gold/10 px-8 py-6 text-lg rounded-none transition-all duration-300 flex items-center gap-2">
                  Get In Touch
                  <ArrowRight size={20} />
                </Button>
              </Link>
              <Link to="/portfolio">
                <Button variant="outline" className="border-2 border-gold text-silver hover:bg-gold/10 px-8 py-6 text-lg rounded-none transition-all duration-300">
                  View Our Portfolio
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EventsExhibitions;

